/**
 * Autosave utility functions for managing single in-flight + trailing save approach.
 * This prevents conflicting saves and ensures data consistency.
 */

/**
 * Creates an autosave manager that handles single in-flight + trailing save logic.
 * This ensures only one save operation runs at a time while queuing additional saves.
 *
 * @param {Function} saveFunction - The actual save function to call
 * @param {Object} options - Configuration options
 * @param {number} options.debounceMs - Debounce delay in milliseconds (default: 2000)
 * @param {Function} options.onStatusChange - Callback for status changes
 * @returns {Object} Autosave manager with save and cancel methods
 */
export const createAutosaveManager = (saveFunction, options = {}) => {
  const { debounceMs = 2000, onStatusChange } = options;

  let saveTimeoutId = null;
  let currentSavePromise = null;
  let hasPendingSave = false;
  let status = 'idle'; // idle | saving | saved | error

  const updateStatus = (newStatus, error = null) => {
    status = newStatus;
    if (onStatusChange) {
      onStatusChange(newStatus, error);
    }
  };

  const executeSave = async () => {
    if (currentSavePromise) {
      // Mark that we need another save after current one completes
      hasPendingSave = true;
      return currentSavePromise;
    }

    updateStatus('saving');

    currentSavePromise = (async () => {
      try {
        await saveFunction();
        updateStatus('saved');
        return true;
      } catch (error) {
        updateStatus('error', error);
        throw error;
      } finally {
        currentSavePromise = null;

        // If there's a pending save, execute it after a short delay
        if (hasPendingSave) {
          hasPendingSave = false;
          setTimeout(() => executeSave(), 100);
        }
      }
    })();

    return currentSavePromise;
  };

  const debouncedSave = () => {
    // Clear any existing timeout
    if (saveTimeoutId) {
      clearTimeout(saveTimeoutId);
    }

    // Set new timeout
    saveTimeoutId = setTimeout(() => {
      saveTimeoutId = null;
      executeSave().catch(() => {
        // Error already handled in executeSave
      });
    }, debounceMs);
  };

  const saveImmediately = () => {
    // Cancel debounced save
    if (saveTimeoutId) {
      clearTimeout(saveTimeoutId);
      saveTimeoutId = null;
    }

    return executeSave();
  };

  const cancel = () => {
    if (saveTimeoutId) {
      clearTimeout(saveTimeoutId);
      saveTimeoutId = null;
    }
    hasPendingSave = false;
    updateStatus('idle');
  };

  const getStatus = () => status;
  const isInFlight = () => currentSavePromise !== null;

  return {
    save: debouncedSave,
    saveNow: saveImmediately,
    cancel,
    getStatus,
    isInFlight,
  };
};

/**
 * Debounce utility that cancels previous calls when dependencies change
 * @param {Function} fn - Function to debounce
 * @param {number} delay - Delay in milliseconds
 * @param {Array} deps - Dependencies array (like useEffect)
 * @returns {Function} Debounced function with cancel method
 */
export const createStableDebouncedFunction = (fn, delay, deps = []) => {
  let timeoutId = null;
  let lastDeps = deps;

  const debouncedFn = (...args) => {
    // Check if dependencies changed
    if (deps.some((dep, index) => dep !== lastDeps[index])) {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      lastDeps = [...deps];
    }

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      timeoutId = null;
      fn(...args);
    }, delay);
  };

  debouncedFn.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return debouncedFn;
};

/**
 * Formats time ago string for save status
 * @param {Date} date - The date to format
 * @returns {string} Formatted time ago string
 */
const formatTimeAgo = (date) => {
  if (!date) return 'Saved';

  const now = new Date();
  const diffMs = now - date;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  if (diffMinutes === 0) return 'Saved just now';
  if (diffMinutes === 1) return 'Saved 1 min ago';
  if (diffMinutes < 60) return `Saved ${diffMinutes} mins ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours === 1) return 'Saved 1 hour ago';
  if (diffHours < 24) return `Saved ${diffHours} hours ago`;

  return 'Saved';
};

/**
 * Status formatter utilities for displaying save status to users
 */
export const formatSaveStatus = (status, lastSavedAt, errorMessage) => {
  switch (status) {
    case 'saving':
      return { type: 'saving', message: 'Saving...', showSpinner: true };
    case 'saved':
      return {
        type: 'saved',
        message: formatTimeAgo(lastSavedAt) || 'Saved',
        showCheckmark: true,
      };
    case 'error':
      return {
        type: 'error',
        message: errorMessage || 'Error saving',
        showError: true,
      };
    case 'idle':
    default:
      return { type: 'idle', message: null };
  }
};
